// Post-sign-in profile setup. After a brand-new user finishes signing in,
// App.tsx routes them here unless they've already picked a Chinese level or
// explicitly hit "Skip for now". The flow is one intro card with two clear
// choices — set me up or skip — followed by the three profile questions if
// they pick the former. Skipping flips `skippedSetup=true` so we never
// re-prompt on this device.
//
// The three-question state machine (level → skills → goals) is the same
// content that used to live inside OnboardingScreen pre-rewrite. It's
// hoisted here so post-auth profile state is separate from the pre-auth
// welcome flow.

import { useState } from 'react';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import type { UserProfile } from '@/lib/types';
import { C } from '@/styles/tokens';

type Step = 'intro' | 'level' | 'skills' | 'goals';

const STEPS: Step[] = ['intro', 'level', 'skills', 'goals'];

type Skills = NonNullable<UserProfile['skills']>;
type SkillKey = keyof Skills;

interface ProfileSetupScreenProps {
  /** Called when the user finishes all four questions. The parent patches
   *  the profile in one shot — same pattern as the old OnboardingScreen. */
  onComplete: (patch: Partial<UserProfile>) => void;
  /** Called when the user picks "Skip for now" on the intro card. */
  onSkip: () => void;
}

const LEVELS: { value: NonNullable<UserProfile['level']>; label: string; sub: string }[] = [
  { value: 'beginner', label: 'I know almost nothing', sub: 'Start with HSK 1 personas' },
  { value: 'lower-intermediate', label: 'A few hundred words', sub: 'HSK 1–2' },
  { value: 'intermediate', label: 'Daily-life conversations', sub: 'HSK 3' },
  { value: 'upper-intermediate', label: 'Comfortable but rusty', sub: 'HSK 3–4' },
  { value: 'advanced', label: 'Reading fluently', sub: 'HSK 4+' },
];

const SKILL_ROWS: { key: SkillKey; label: string; sub: string }[] = [
  { key: 'read', label: 'Read', sub: 'Recognising characters' },
  { key: 'write', label: 'Write', sub: 'Writing characters by hand' },
  { key: 'listen', label: 'Listen', sub: 'Understanding spoken Chinese' },
  { key: 'speak', label: 'Speak', sub: 'Holding a conversation' },
];

const GOALS = [
  'Travel',
  'Talking to family',
  'Work',
  'Reading',
  'Watching shows',
  'Just curious',
] as const;

export function ProfileSetupScreen({ onComplete, onSkip }: ProfileSetupScreenProps) {
  const [step, setStep] = useState<Step>('intro');
  const [level, setLevel] = useState<UserProfile['level'] | undefined>(undefined);
  const [skills, setSkills] = useState<Partial<Skills>>({});
  const [goals, setGoals] = useState<string[]>([]);

  const stepIndex = STEPS.indexOf(step);
  const skillsComplete = SKILL_ROWS.every(({ key }) => typeof skills[key] === 'number');

  function advance() {
    const next = STEPS[stepIndex + 1];
    if (next) setStep(next);
  }
  function back() {
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  }

  function toggleGoal(g: string) {
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  }

  function setSkill(key: SkillKey, value: number) {
    setSkills((prev) => ({ ...prev, [key]: value }));
  }

  function finish() {
    if (!level || !skillsComplete) return;
    const patch: Partial<UserProfile> = { level, skills: skills as Skills, goals };
    onComplete(patch);
  }

  return (
    <section
      className="h-[100dvh] flex flex-col safe-top safe-bottom px-6 pt-4 pb-6"
      style={{ background: C.cream }}
    >
      <header className="flex items-center justify-between mb-6 min-h-[28px]">
        {step !== 'intro' ? (
          <button
            type="button"
            onClick={back}
            className="-ml-2 p-2 rounded-full inline-flex items-center"
            style={{ color: C.inkLight }}
            aria-label="Previous step"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
        ) : (
          <span aria-hidden className="w-6" />
        )}

        <div
          className="flex items-center gap-1.5"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-valuenow={stepIndex + 1}
        >
          {STEPS.map((s, i) => (
            <span
              key={s}
              aria-hidden
              className="block rounded-full transition-all"
              style={{
                width: i === stepIndex ? 18 : 6,
                height: 6,
                background: i <= stepIndex ? C.vermillion : C.mist,
              }}
            />
          ))}
        </div>

        <span aria-hidden className="w-6" />
      </header>

      {step === 'intro' && <IntroStep onSetUp={advance} onSkip={onSkip} />}

      {step === 'level' && (
        <div className="flex-1 flex flex-col">
          <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
            How much Chinese do you have?
          </div>
          <div className="ff-body text-[14px] leading-relaxed mb-6" style={{ color: C.inkLight }}>
            We use this to pick personas you can actually read.
          </div>
          <div className="space-y-2 flex-1 overflow-y-auto">
            {LEVELS.map((l, i) => {
              const selected = level === l.value;
              return (
                <button
                  type="button"
                  key={l.value}
                  onClick={() => setLevel(l.value)}
                  className="w-full p-4 rounded-2xl text-left flex items-center gap-3 anim-slide-up"
                  style={{
                    background: selected ? C.blush : 'white',
                    boxShadow: '0 1px 4px rgba(26,22,20,0.04)',
                    border: `2px solid ${selected ? C.vermillion : 'transparent'}`,
                    animationDelay: `${i * 30}ms`,
                  }}
                  aria-pressed={selected}
                >
                  <div className="flex-1">
                    <div className="ff-body font-semibold" style={{ color: C.ink }}>
                      {l.label}
                    </div>
                    <div className="ff-body text-[12px]" style={{ color: C.inkLight }}>
                      {l.sub}
                    </div>
                  </div>
                  {selected && <Check size={16} style={{ color: C.vermillionDeep }} aria-hidden />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              if (!level) return;
              advance();
            }}
            disabled={!level}
            className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: C.vermillion }}
          >
            Next
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      )}

      {step === 'skills' && (
        <div className="flex-1 flex flex-col">
          <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
            Rate your Chinese skill.
          </div>
          <div className="ff-body text-[14px] leading-relaxed mb-6" style={{ color: C.inkLight }}>
            1 = beginner, 5 = native. Be honest — we&apos;ll lean into your strengths.
          </div>
          <div className="space-y-4 flex-1 overflow-y-auto">
            {SKILL_ROWS.map(({ key, label, sub }, i) => (
              <div
                key={key}
                className="p-4 rounded-2xl anim-slide-up"
                style={{
                  background: 'white',
                  boxShadow: '0 1px 4px rgba(26,22,20,0.04)',
                  animationDelay: `${i * 30}ms`,
                }}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className="ff-body font-semibold" style={{ color: C.ink }}>
                    {label}
                  </span>
                  <span className="ff-body text-[11px]" style={{ color: C.inkLight }}>
                    {sub}
                  </span>
                </div>
                <div className="flex gap-2" role="radiogroup" aria-label={`Rate ${label} from 1 to 5`}>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const current = skills[key];
                    const selected = current === n;
                    return (
                      <button
                        type="button"
                        key={n}
                        onClick={() => setSkill(key, n)}
                        className="flex-1 py-2 rounded-full ff-body text-[13px] font-semibold transition-colors"
                        role="radio"
                        aria-checked={selected}
                        aria-label={`${label}: ${n} of 5`}
                        style={{
                          background: selected ? C.vermillion : C.paper,
                          color: selected ? 'white' : C.ink,
                          border: `1px solid ${selected ? C.vermillion : C.mist}`,
                        }}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              if (!skillsComplete) return;
              advance();
            }}
            disabled={!skillsComplete}
            className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: C.vermillion }}
          >
            Next
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      )}

      {step === 'goals' && (
        <div className="flex-1 flex flex-col">
          <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
            Why are you learning?
          </div>
          <div className="ff-body text-[14px] leading-relaxed mb-6" style={{ color: C.inkLight }}>
            Pick any that fit — your friends will lean into these.
          </div>
          <div className="flex flex-wrap gap-2 flex-1 content-start">
            {GOALS.map((g, i) => {
              const selected = goals.includes(g);
              return (
                <button
                  type="button"
                  key={g}
                  onClick={() => toggleGoal(g)}
                  className="px-4 py-2 rounded-full ff-body font-medium anim-slide-up"
                  style={{
                    background: selected ? C.vermillion : 'white',
                    color: selected ? 'white' : C.ink,
                    border: `1px solid ${selected ? C.vermillion : C.blush}`,
                    animationDelay: `${i * 30}ms`,
                  }}
                  aria-pressed={selected}
                >
                  {g}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={finish}
            className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2"
            style={{ background: C.vermillion }}
          >
            Done
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      )}
    </section>
  );
}

function IntroStep({ onSetUp, onSkip }: { onSetUp: () => void; onSkip: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center text-center">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm">
        <div
          aria-hidden
          className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 anim-scale-in"
          style={{
            background: C.vermillion,
            boxShadow: '0 8px 22px rgba(233,75,60,0.32)',
          }}
        >
          <span className="ff-zh-serif text-white text-4xl font-bold leading-none">字</span>
        </div>
        <h1 className="ff-display italic text-4xl" style={{ color: C.ink }}>
          Time to master Hanzi!
        </h1>
      </div>
      <div className="w-full flex flex-col gap-3">
        <button
          type="button"
          onClick={onSetUp}
          className="w-full py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2"
          style={{ background: C.vermillion }}
        >
          Set me up
          <ChevronRight size={16} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="ff-body text-[12px] underline underline-offset-4 decoration-1 py-2 px-3"
          style={{ background: 'transparent', color: C.inkSofter }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
